import { IStateParamsService } from 'angular-ui-router';
import { includes } from 'lodash';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { SupportStatuses } from '../../../ng2/shared/constants/support-statuses.constant';
import { USER_ROLE_PERMISSIONS_FOR_STATES } from '../../../ng2/shared/constants/user-role-permissions-for-states.constant';
import { ISchoolSupport } from '../../../ng2/shared/typings/interfaces/support.interface';
import { MySupportsCtrl } from '../../features/my-supports/my-supports.controller';
import { SupportOverviewCtrl } from '../../features/support-overview/support-overview.controller';
import { AuthorizationDeniedError } from './../../../ng2/shared/helpers/authorization-denied-error/authorization-denied-error.helper';
import { ImSupport } from './../../shared/static-models/im-models/im-support.service';

angular.module('nv.routes.main').config(configBlock);

/* istanbul ignore next */
function configBlock($stateProvider) {
  const permissionedSupportResolve = [
    'Support',
    '$stateParams',
    'currentUser',
    'ImUser',
    (Support, $stateParams: IStateParamsService, currentUser, imUser: ImUser) => {
      const USERS_THAT_CAN_ONLY_ACCESS_SUPPORTS_THEY_LEAD = ['edit_caseload', 'view_caseload', 'view_all'];
      const userRoleType = imUser.getRoleTypeOnAllLevels(currentUser);
      const userCanOnlyAccessSupportsTheyLead = includes(USERS_THAT_CAN_ONLY_ACCESS_SUPPORTS_THEY_LEAD, userRoleType);
      const id = $stateParams.supportId;

      /*
        This is a unique case:
       All users can view all school supports, but in this view, we want to limit
       `USERS_THAT_CAN_ONLY_ACCESS_SUPPORTS_THEY_LEAD` to only view school supports the lead.
        Hence, why we can first fetch the support and then reject the promise if needed to
        prevent the user from accessing the view (CM).
      */
      return Support.get({ id }).$promise.then(support => {
        if (support && userCanOnlyAccessSupportsTheyLead) {
          const { _id: userId } = currentUser;
          const activityLeadUserId = support.activityLead.userId;
          const userIsActivityLead = userId === activityLeadUserId;

          if (!userIsActivityLead) throw new AuthorizationDeniedError();
        }

        return support;
      });
    },
  ];

  $stateProvider
    .state('main.school.my-supports', {
      url: '/my-supports',
      views: {
        '': {
          controller: MySupportsCtrl,
          controllerAs: '$ctrl',
          template: require('../../features/my-supports/my-supports.html'),
        },
        'sidenav@main.school.my-supports': {
          // TODO when refactored, where will currentUser be pulled from. Currently injected
          template: `
            <nvps-side-nav
              [current-user]='$ctrl.currentUser'
            ></nvps-side-nav>
          `,
        },
        'contentTools@main.school.my-supports': {
          template: `
            <content-tools></content-tools>
          `,
        },
      },
      resolve: {
        supportsForCurrentUser: [
          'Support',
          'currentUser',
          'currentSchool',
          'ImSupport',
          (Support, currentUser, currentSchool, imSupport: ImSupport) => {
            const schoolId = currentSchool._id;

            return new Promise((resolve, reject) => {
              // A user should see not started, in progress, or completed (current school year) supports in their My Supports list (SR)
              Support.queryByUserRoleForThisSYSupports(currentUser, schoolId)
                .$promise.then((supports: ISchoolSupport[]) => {
                  const supportsInProgressOrNotStartedOrCompleted = supports.filter(support => {
                    const status = imSupport.getFrontendStatus(support);
                    const supportIsInProgressOrNotStartedOrCompleted =
                      status === SupportStatuses.frontend.IN_PROGRESS ||
                      status === SupportStatuses.frontend.NOT_STARTED ||
                      status === SupportStatuses.frontend.COMPLETED;

                    return supportIsInProgressOrNotStartedOrCompleted;
                  });

                  resolve(supportsInProgressOrNotStartedOrCompleted);
                })
                .catch(reject);
            });
          },
        ],
      },
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.my-supports'],
      },
    })

    .state('main.school.support-attendance-form', {
      views: {
        '': {
          template: require('../../features/support-attendance-form/support-attendance-form.html'),
          controller: 'SupportAttendanceFormCtrl',
          controllerAs: 'ctrl',
        },
        'sideNav@main.school.support-attendance-form': {
          template: `
            <nvps-side-nav
              [current-user]='ctrl.currentUser'
            ></nvps-side-nav>
          `,
        },
        'contentTools@main.school.support-attendance-form': {
          template: `
            <content-tools></content-tools>
          `,
        },
      },
      url: '/supports/:supportId/attendance?eventDate&edit&isUnlogged',
      params: {
        supportId: null,
        eventDate: null,
        edit: 'false', // Whether or not to open form in edit mode
        isUnlogged: 'true', // Whether or not to load previously logged data
      },
      resolve: {
        // TODO: resolve from ngrx - update support attendnace implementation - JYR
        studentSupports: [
          'StudentSupport',
          '$stateParams',
          (StudentSupport, $stateParams: IStateParamsService) => {
            const supportId = $stateParams.supportId;
            return StudentSupport.queryBySupportId(supportId).$promise;
          },
        ],
        support: permissionedSupportResolve,
        attendanceRecords: [
          'AttendanceRecord',
          '$stateParams',
          'support', // we inject support to ensure that the user has access to the support (CM).
          (AttendanceRecord, $stateParams: IStateParamsService, support) => {
            const supportId = $stateParams.supportId;
            const eventDate = $stateParams.eventDate;
            return AttendanceRecord.queryBySupportIdAndEventDate(supportId, eventDate).$promise;
          },
        ],
      },
    })

    .state('main.school.support-overview', {
      url: '/supports/:supportId/overview',
      views: {
        '': {
          template: require('../../features/support-overview/support-overview.html'),
          controller: SupportOverviewCtrl,
          controllerAs: 'ctrl',
        },
        'sideNav@main.school.support-overview': {
          template: `
            <nvps-side-nav
              [current-user]='ctrl.currentUser'
            ></nvps-side-nav>
          `,
        },
        'contentTools@main.school.support-overview': {
          template: `
            <content-tools></content-tools>
          `,
        },
      },
      params: {
        supportId: null,
      },
      resolve: {
        // TODO: resolve from ngrx - update support overview implementation - JYR
        studentSupports: [
          'StudentSupport',
          '$stateParams',
          (StudentSupport, $stateParams: IStateParamsService) => {
            const supportId = $stateParams.supportId;
            return StudentSupport.queryBySupportId(supportId).$promise.then(
              StudentSupport.getActiveAndCompletedStudentSupports,
            );
          },
        ],
        support: permissionedSupportResolve,
        attendanceRecords: [
          'support', // we inject support to ensure that the user has access to the support (CM).
          'AttendanceRecord',
          (support, AttendanceRecord) => {
            const supportId = support._id;
            return AttendanceRecord.queryBySupportId(supportId).$promise;
          },
        ],
      },
    });
}
